import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { connectToHubUser } from "src/redux/slices/hub";
import { startMonitoring, stopMonitoring } from "src/redux/slices/errors";
import CreateConnection from "src/utils/hubs/HubMatrix";
import buildHubUrl from "src/utils/hubs/buildHubUrl";
import axios from "src/utils/axios";
import { EXPIRES_KEY, ORIGIN_KEY, ttlState, ttlMinutesFrom, debugSessionSyncAction } from "src/utils/debug/monitorPolicy";
import { refreshRecordingFlag } from "src/utils/debug/debugRecorder";

function readExpiresAt() {
  try { return window.localStorage.getItem(EXPIRES_KEY); } catch { return null; }
}
function readOrigin() {
  try { return window.localStorage.getItem(ORIGIN_KEY); } catch { return null; }
}
function writeExpiresAt(iso) {
  try {
    if (iso) window.localStorage.setItem(EXPIRES_KEY, iso);
    else window.localStorage.removeItem(EXPIRES_KEY);
  } catch { /* ignore */ }
}

// Una sola conexión/escucha por pestaña aunque varios layouts monten el hook.
let listening = false;

/**
 * Escucha el evento `monitorToggle/{idRestaurante}` que emite el backend
 * cuando un admin activa/desactiva la sesión de depuración. Al recibirlo,
 * este dispositivo arranca/para SU monitorización local.
 *
 * TTL (Fase 5): la caducidad se guarda en localStorage (`APIMONITOR_EXPIRES_AT`)
 * y se respeta tras recargar. Antes era un setTimeout que se perdía con F5
 * mientras el flag `APIMONITOR_ENABLED=1` seguía → grabación indefinida.
 */
export default function useApiMonitorRemoteToggle() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.authJwt);
  const isMonitoring = useSelector((s) => s.errors?.isMonitoring);
  const ttlTimerRef = useRef(null);
  const ownsRef = useRef(false);

  // Caducidad pendiente (incluida la heredada de antes de recargar).
  useEffect(() => {
    if (ttlTimerRef.current) {
      clearTimeout(ttlTimerRef.current);
      ttlTimerRef.current = null;
    }
    if (!isMonitoring) return undefined;
    const st = ttlState(Date.now(), readExpiresAt());
    if (!st.hasTtl) return undefined;
    if (st.expired) {
      writeExpiresAt(null);
      dispatch(stopMonitoring());
      refreshRecordingFlag();
      return undefined;
    }
    ttlTimerRef.current = setTimeout(() => {
      ttlTimerRef.current = null;
      writeExpiresAt(null);
      dispatch(stopMonitoring());
      refreshRecordingFlag();
    }, st.remainingMs);
    return () => {
      if (ttlTimerRef.current) clearTimeout(ttlTimerRef.current);
      ttlTimerRef.current = null;
    };
  }, [isMonitoring, dispatch]);

  // Al arrancar, al volver a la pestaña y al recuperar red: preguntar al
  // servidor si hay sesión de depuración. Sin esto, un dispositivo abierto
  // después de activarla (o que perdió el aviso) nunca grababa y el panel
  // de Live debug quedaba vacío.
  const monitoringRef = useRef(isMonitoring);
  monitoringRef.current = isMonitoring;
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    let lastAt = 0;
    const sync = async () => {
      const now = Date.now();
      if (now - lastAt < 15_000) return;
      lastAt = now;
      try {
        const res = await axios.get("api-monitor/debug-session");
        if (cancelled) return;
        const action = debugSessionSyncAction(res?.data, {
          isMonitoring: !!monitoringRef.current,
          expiresAtIso: readExpiresAt(),
          origin: readOrigin(),
        });
        if (action === "start") {
          writeExpiresAt(res.data.expiresAt || null);
          dispatch(startMonitoring({ origin: "remote" }));
          refreshRecordingFlag();
        } else if (action === "stop") {
          writeExpiresAt(null);
          dispatch(stopMonitoring());
          refreshRecordingFlag();
        }
      } catch {
        /* sin red o API antigua: seguimos con el aviso en tiempo real */
      }
    };
    sync();
    const onVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", sync);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", sync);
    };
  }, [user?.id, dispatch]);

  useEffect(() => {
    if (!user?.id || listening) return undefined;
    listening = true;
    ownsRef.current = true;

    const handleToggle = (payload) => {
      // Payload: { enabled, ttlMinutes, requestedBy, requestedAtUtc }.
      const enabled = !!payload?.enabled;
      if (enabled) {
        const expires = new Date(Date.now() + ttlMinutesFrom(payload) * 60 * 1000).toISOString();
        writeExpiresAt(expires);
        dispatch(startMonitoring({ origin: "remote" }));
      } else {
        writeExpiresAt(null);
        dispatch(stopMonitoring());
      }
      refreshRecordingFlag();
    };

    const cleanup = CreateConnection(
      buildHubUrl("/hub/userhub", { restauranteId: user.id }),
      [
        {
          id: `monitorToggle/${user.id}`,
          operator: handleToggle,
        },
      ],
      dispatch,
      connectToHubUser,
      "user",
    );

    return () => {
      cleanup();
      if (ownsRef.current) listening = false;
      ownsRef.current = false;
    };
  }, [user?.id, dispatch]);
}
